/**
 * FAQ entries, legal pages, and social links.
 *
 * Three tabs from one factory, because the three endpoints are the same
 * endpoint three times: an unpaginated GET, a POST, a PATCH by id, a DELETE by
 * id, over a flat list of rows with a handful of scalar fields. Writing them
 * out separately would be three copies of the same fetch-render-submit loop
 * differing only in field names — and the next time one of them grew a
 * behaviour, only one copy would get it.
 *
 * News is NOT built from this factory even though it is also CRUD: it
 * paginates, it uploads an image, and its slug is generated server-side from
 * the title. See `news-tab.ts`.
 *
 * ─── Editing happens in place ───────────────────────────────────────────────
 * Clicking Edit turns that row's cells into inputs rather than opening a
 * dialog. Legal pages carry a whole policy document in `content`, so a modal
 * would be a small box around a large thing; and with the row expanded in
 * place the neighbouring rows stay visible, which is what sort-order editing
 * actually needs.
 */
import {
  createFaq, createLegal, createSocialLink, deleteFaq, deleteLegal, deleteSocialLink,
  listFaq, listLegal, listSocialLinks, updateFaq, updateLegal, updateSocialLink,
} from '../admin-api';
import type { FaqEntry, LegalPage, SocialLink } from '../admin-api.types';
import {
  badge, checkbox, errorText, esc, field, readForm, section,
} from '../admin-kit';
import type { AdminTab, TabContext } from '../admin-kit';

interface FieldSpec {
  key: string;
  label: string;
  long?: boolean;
  type?: string;
  hint?: string;
}

interface CollectionSpec<T extends { id: string }> {
  id: string;
  label: string;
  group: string;
  title: string;
  subtitle: string;
  /** Column shown as the row's heading when collapsed. */
  summary(row: T): string;
  fields: FieldSpec[];
  /** Name of the boolean column that decides whether the row is live. */
  activeKey: keyof T & string;
  list(): Promise<T[]>;
  create(body: Record<string, unknown>): Promise<unknown>;
  update(id: string, body: Record<string, unknown>): Promise<unknown>;
  remove(id: string): Promise<unknown>;
  /** What the delete confirmation warns about. */
  deleteWarning: string;
}

/**
 * Coerces a form's strings into the types the DTOs validate against.
 *
 * `sortOrder` is `@IsInt()` on the backend, and an `<input type="number">`
 * still hands back a string — posting "3" fails validation with a message that
 * reads like a bug in the API rather than a type in the form.
 */
function coerce(values: Record<string, string | boolean>, fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    const spec = fields.find((f) => f.key === key);
    if (spec?.type === 'number') {
      const n = Number(value);
      out[key] = Number.isFinite(n) ? Math.trunc(n) : 0;
    } else {
      out[key] = value;
    }
  }
  return out;
}

function makeCollectionTab<T extends { id: string }>(spec: CollectionSpec<T>): AdminTab {
  return {
    id: spec.id,
    label: spec.label,
    group: spec.group,

    mount(host: HTMLElement, ctx: TabContext) {
      let live = true;
      let rows: T[] | null = null;
      let error = '';
      /** The row being edited, the string 'new' while creating, or null. */
      let editing: string | null = null;

      function form(row: T | null): string {
        const values = (row ?? {}) as Record<string, unknown>;
        return `
          <form class="tg-adm-form" data-form="${row ? esc(row.id) : 'new'}">
            ${spec.fields.map((f) => field(f.key, f.label, values[f.key] ?? '', {
    long: f.long,
    type: f.type,
    hint: f.hint,
  })).join('')}
            ${checkbox(spec.activeKey, 'Live on the site', row ? Boolean(values[spec.activeKey]) : true)}
            <div class="tg-adm-actions">
              <button type="submit" class="tg-adm-btn tg-adm-btn-primary">${row ? 'Save' : 'Create'}</button>
              <button type="button" class="tg-adm-btn" data-act="cancel">Cancel</button>
              ${row ? `<button type="button" class="tg-adm-btn tg-adm-btn-danger"
                data-act="delete" data-id="${esc(row.id)}">Delete</button>` : ''}
            </div>
          </form>`;
      }

      function body(): string {
        if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
        if (!rows) return '<p class="tg-adm-empty">Loading…</p>';

        const creating = editing === 'new' ? form(null) : '';
        if (!rows.length && !creating) {
          return '<p class="tg-adm-empty">Nothing here yet.</p>';
        }

        const list = rows.map((row) => {
          if (editing === row.id) return form(row);
          const isLive = Boolean((row as Record<string, unknown>)[spec.activeKey]);
          return `
            <div class="tg-adm-cmsrow">
              <div class="tg-adm-cmsrow-main">
                ${isLive ? badge('live', 'ok') : badge('hidden', 'mute')}
                <span>${spec.summary(row)}</span>
              </div>
              <button type="button" class="tg-adm-btn" data-act="edit" data-id="${esc(row.id)}">Edit</button>
            </div>`;
        }).join('');

        return creating + list;
      }

      const paint = () => {
        host.innerHTML = section(
          spec.title,
          spec.subtitle,
          body(),
          '<button type="button" class="tg-adm-btn tg-adm-btn-primary" data-act="new">Add</button>',
        );
      };

      async function load(): Promise<void> {
        error = '';
        try {
          const result = await spec.list();
          if (!live) return;
          rows = result;
        } catch (err) {
          if (!live) return;
          error = errorText(err);
        }
        paint();
      }

      async function act(what: string, run: () => Promise<unknown>): Promise<void> {
        try {
          await run();
          if (!live) return;
          editing = null;
          ctx.notify(what);
        } catch (err) {
          if (!live) return;
          ctx.notify(errorText(err), 'err');
          return;
        }
        await load();
      }

      const onClick = (e: MouseEvent) => {
        const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
        switch (el?.dataset.act) {
          case 'new':
            editing = 'new';
            paint();
            break;
          case 'edit':
            editing = el!.dataset.id!;
            paint();
            break;
          case 'cancel':
            editing = null;
            paint();
            break;
          case 'delete': {
            const id = el!.dataset.id!;
            ctx.confirm({
              title: 'Delete this entry?',
              body: spec.deleteWarning,
              confirmLabel: 'Delete',
              onConfirm: () => void act('Deleted.', () => spec.remove(id)),
            });
            break;
          }
          default:
            break;
        }
      };

      const onSubmit = (e: SubmitEvent) => {
        const formEl = e.target as HTMLFormElement;
        const target = formEl.dataset.form;
        if (!target) return;
        e.preventDefault();
        const payload = coerce(readForm(formEl), spec.fields);
        if (target === 'new') {
          void act('Created.', () => spec.create(payload));
        } else {
          void act('Saved.', () => spec.update(target, payload));
        }
      };

      host.addEventListener('click', onClick);
      host.addEventListener('submit', onSubmit as EventListener);

      paint();
      void load();

      return () => {
        live = false;
        host.removeEventListener('click', onClick);
        host.removeEventListener('submit', onSubmit as EventListener);
      };
    },
  };
}

export const faqTab = makeCollectionTab<FaqEntry>({
  id: 'faq',
  label: 'FAQ',
  group: 'CMS',
  title: 'FAQ',
  subtitle: 'Questions and answers, shown on the public site in sort order.',
  summary: (r) => esc(r.question),
  activeKey: 'active',
  fields: [
    { key: 'question', label: 'Question' },
    { key: 'answer', label: 'Answer', long: true },
    { key: 'sortOrder', label: 'Sort order', type: 'number', hint: 'Lower comes first' },
  ],
  list: listFaq,
  create: (b) => createFaq(b as Partial<FaqEntry>),
  update: (id, b) => updateFaq(id, b as Partial<FaqEntry>),
  remove: deleteFaq,
  deleteWarning: 'The question disappears from the public FAQ immediately.',
});

export const legalTab = makeCollectionTab<LegalPage>({
  id: 'legal',
  label: 'Legal pages',
  group: 'CMS',
  title: 'Legal pages',
  subtitle: 'Terms, privacy and the rest. The slug is the URL: /legal/<slug>.',
  summary: (r) => `${esc(r.title)} <span class="tg-adm-mono">/legal/${esc(r.slug)}</span>`,
  activeKey: 'active',
  fields: [
    { key: 'slug', label: 'Slug', hint: 'Only the slugs the app knows about are routable' },
    { key: 'title', label: 'Title' },
    { key: 'content', label: 'Content', long: true, hint: 'Markdown — rendered by the Settings screen and the landing page' },
  ],
  list: listLegal,
  create: (b) => createLegal(b as Partial<LegalPage>),
  update: (id, b) => updateLegal(id, b as Partial<LegalPage>),
  remove: deleteLegal,
  deleteWarning: 'Any link to this policy starts failing closed — the sheet opens and says the document is missing.',
});

export const socialLinksTab = makeCollectionTab<SocialLink>({
  id: 'social',
  label: 'Social links',
  group: 'CMS',
  title: 'Social links',
  subtitle: 'The icons in the landing page footer.',
  summary: (r) => `${esc(r.platform)} <span class="tg-adm-mono">${esc(r.url)}</span>`,
  activeKey: 'active',
  fields: [
    { key: 'platform', label: 'Platform' },
    { key: 'url', label: 'URL' },
    { key: 'icon', label: 'Icon', hint: 'Icon name, if the footer uses one' },
    { key: 'sortOrder', label: 'Sort order', type: 'number', hint: 'Lower comes first' },
  ],
  list: listSocialLinks,
  create: (b) => createSocialLink(b as Partial<SocialLink>),
  update: (id, b) => updateSocialLink(id, b as Partial<SocialLink>),
  remove: deleteSocialLink,
  deleteWarning: 'The link vanishes from the footer.',
});
